import { BackwardIcon, ClipboardDocumentIcon, PencilIcon, PlayIcon, TrashIcon } from "@heroicons/react/24/solid";
import { Avatar } from "@moe/oss/ui/avatar";
import { cn } from "@moe/oss/utils/utils";
import { UserMessage as UserMessageI } from "@moe/priv/model/message";
import { useDataContext } from "@web/route-services/chat/data-context";
import { Display } from "@web/route-services/chat/message/Display";
import { EditingHint } from "@web/route-services/chat/message/EditingHint";
import { useServiceContext } from "@web/route-services/chat/service-context";
import { forwardRef, useState } from "react";
import { toast } from "sonner";
import { OptionsButton } from "./Dropdown";
import { baseMessageEditStyles, baseMessageStyles } from "./Message";

interface UserMessageProps {
  message: UserMessageI;
  isBottom: boolean;
  className?: string;
  charName?: string;
}

export const UserMessage = forwardRef<HTMLDivElement, UserMessageProps>(
  ({ message, isBottom, className, charName }, ref) => {
  const { persona } = useDataContext();
  const { service, editingMessageID } = useServiceContext();
  const isEditing = editingMessageID === message.id;
  const [editText, setEditText] = useState(message.text);

  const actions = [
    {
      label: "Copy",
      icon: ClipboardDocumentIcon,
      onClick: () => {
        navigator.clipboard.writeText(message.text);
        toast.success("Message copied to clipboard!");
      }
    },
    {
      label: "Edit",
      icon: PencilIcon,
      onClick: () => {
        service.edit(message.id);
      }
    },
    {
      label: "Continue",
      icon: PlayIcon,
      onClick: async () => {
        service.continue();
      },
      show: isBottom
    },
    {
      label: "Rewind",
      icon: BackwardIcon,
      onClick: async () => {
        service.rewind(message.id);
      },
      show: !isBottom
    },
    {
      label: "Delete",
      icon: TrashIcon,
      onClick: () => {
        service.delete(message.id);
      }
    }
  ];

  return (
    <div
      ref={ref}
      className={cn("md:max-w-3/4 text-chat-user-message-text flex shrink-0 flex-col self-end", className)}
    >
      <div
        className={cn(
          baseMessageStyles,
          isEditing ? baseMessageEditStyles : "",
          "bg-chat-user-message-background border-line border pt-2 md:pt-3"
        )}
      >
        <Avatar src={persona.avatar} className="" />
        <div className="flex flex-col justify-start space-y-0.5">
          <div className="text-tx-secondary flex h-fit flex-row items-center justify-between space-x-3">
            <p className="text-tx-primary font-semibold">{persona.name}</p>
            <OptionsButton actions={actions} />
          </div>
          <div>
            <Display
              charName={charName}
              setEditText={setEditText}
              editText={editText}
              message={message}
              isEditing={isEditing}
            />
          </div>
        </div>
      </div>
      {isEditing && <EditingHint message={message} editText={editText} />}
    </div>
  );
});
